/**
 * Build Alibaba-style keyword autocomplete from source text (titles, names, etc.).
 *
 * @param {string} query
 * @param {string[]} sourceTexts
 * @param {number} [limit]
 * @returns {{ id: string, label: string, type: 'query' }[]}
 */
export function buildQueryCompletions(query, sourceTexts, limit = 8) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return [];

  const seen = new Set();
  /** @type {{ id: string, label: string, type: 'query' }[]} */
  const results = [];

  const add = (label) => {
    const text = String(label || '').replace(/\s+/g, ' ').trim();
    const key = text.toLowerCase();
    if (!text || key === q || seen.has(key) || !key.includes(q)) return;
    seen.add(key);
    results.push({ id: key, label: text, type: 'query' });
  };

  for (const raw of sourceTexts) {
    const text = String(raw || '').replace(/\s+/g, ' ').trim();
    if (!text.toLowerCase().includes(q)) continue;

    const lower = text.toLowerCase();
    const idx = lower.indexOf(q);
    const fromMatch = text.slice(idx);
    const tokens = fromMatch.split(/\s+/).filter(Boolean);

    for (let n = 1; n <= Math.min(4, tokens.length); n += 1) {
      const prefix = text.slice(0, idx);
      const phrase = `${prefix}${tokens.slice(0, n).join(' ')}`.replace(/\s+/g, ' ').trim();
      if (phrase.length <= 72) add(phrase);
    }

    if (text.length <= 48) add(text);
  }

  results.sort((a, b) => {
    const aStarts = a.label.toLowerCase().startsWith(q) ? 0 : 1;
    const bStarts = b.label.toLowerCase().startsWith(q) ? 0 : 1;
    if (aStarts !== bStarts) return aStarts - bStarts;
    return a.label.length - b.label.length;
  });

  return results.slice(0, limit);
}

/**
 * Split a suggestion label for Alibaba-style highlight (typed = muted, completion = bold).
 *
 * @param {string} label
 * @param {string} query
 */
export function splitQueryHighlight(label, query) {
  const q = String(query || '').trim();
  const text = String(label || '');
  if (!q) {
    return { typed: '', completion: text };
  }

  const lower = text.toLowerCase();
  const qLower = q.toLowerCase();
  const idx = lower.indexOf(qLower);
  if (idx === -1) {
    return { typed: text, completion: '' };
  }

  return {
    typed: text.slice(0, idx + q.length),
    completion: text.slice(idx + q.length),
  };
}
